import React from 'react'
import styled, { keyframes } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { sizes } from '../utils/breakpoints'
import { useMediaQuery } from 'react-responsive'
import LineaSVG from '../assets/images/LineaCurvaSiteMap.svg'
import LineaMobileSVG from '../assets/images/LineaCurvaMobileSiteMap.svg'
import LinkdinMobile from '../assets/images/linkdinMobile.svg'
import LinkdinDesktop from '../assets/images/linkdinDesktop.svg'
import MailIcon from '../components/MailIcon'

const ContactUs = () => {
  const isMobile = useMediaQuery({ maxWidth: sizes.mobile })
  const linea = isMobile ? LineaMobileSVG : LineaSVG
  const linkedin = isMobile ? LinkdinMobile : LinkdinDesktop
  const { t } = useTranslation()

  return (
    <Container>
      <PageTitle>{t('contacto.title')}</PageTitle>
      <ImgLinea src={linea} alt="" />

      <Card>
        <CardLeft>
          <CardTitle>{t('contacto.cardTitle')}</CardTitle>
          {t('contacto.subheadParagraphs', { returnObjects: true }).map(
            (paragraph) => (
              <CardText key={paragraph}>{paragraph}</CardText>
            )
          )}
        </CardLeft>

        <CardRight>
          <ContactRowsGroup>
            <ContactRow
              href="https://www.linkedin.com/company/tartis/posts/?feedView=all"
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconBadge>
                <Icon src={linkedin} alt="" />
              </IconBadge>
              LinkedIn
            </ContactRow>
            <ContactRow href="mailto:info@tartis.io">
              <IconBadge>
                <IconWrapper>
                  <MailIcon />
                </IconWrapper>
              </IconBadge>
              info@tartis.io
            </ContactRow>
          </ContactRowsGroup>
        </CardRight>
      </Card>
    </Container>
  )
}

export default ContactUs

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 24px 40px;

  @media (max-width: ${sizes.mobile}px) {
    padding: 0 20px 60px;
  }
`

const PageTitle = styled.h1`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 40px;
  line-height: 48px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 40px;
  text-align: center;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 24px;
    line-height: 28.8px;
    margin: 0 0 28px;
  }
`

const ImgLinea = styled.img`
  position: absolute;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  max-width: 100vw;
  height: auto;
  z-index: 0;

  @media (max-width: ${sizes.mobile}px) {
    position: static;
    transform: none;
    left: auto;
    margin: 12px 0 32px calc(-50vw + 50%);
  }
`

const dropIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-28px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 980px;
  background-color: ${({ theme }) => theme.colors.background.card};
  border-radius: 24px;
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: 64px 72px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 72px;
  animation: ${dropIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: ${sizes.mobile}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 32px;
    padding: 32px 24px;
    border-radius: 16px;
  }
`

const CardLeft = styled.div`
  flex: 1;
  text-align: left;
`

const CardTitle = styled.p`
  color: ${({ theme }) => theme.colors.text.strong};
  font-family: ${({ theme }) => theme.fonts.headingAlt};
  font-size: 26px;
  margin: 0 0 14px;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 19px;
    margin: 0 0 10px;
  }
`

const CardText = styled.p`
  max-width: 340px;
  text-align: justify;
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 15px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 14px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: ${sizes.mobile}px) {
    max-width: none;
    font-size: 13px;
  }
`

const CardRight = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${sizes.mobile}px) {
    justify-content: flex-start;
    width: 100%;
  }
`

const ContactRowsGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 22px;
`

const ContactRow = styled.a`
  display: flex;
  align-items: center;
  gap: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 16px;
  text-decoration: none;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: ${sizes.mobile}px) {
    font-size: 14px;
  }
`

const IconBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background-color: ${({ theme }) => theme.colors.brand.green};
  box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.border.subtle};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const Icon = styled.img`
  width: 24px;
  height: 24px;
`

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;

  svg {
    width: 100%;
    height: 100%;
  }
`
